import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Popover, PageHeader, Row, Col } from 'antd';
import { SettingOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import OnTraccrButton from '../common/buttons/OnTraccrButton';
import OnTraccrCheckbox from '../common/inputs/OnTraccrCheckbox';

const GLOBAL_MATERIAL_COLUMN_OPTIONS = [
  { label: 'ID', value: 'code' },
  { label: 'Description', value: 'description' },
  { label: 'Part Number', value: 'partNumber' },
  { label: 'Supplier', value: 'supplier' },
  { label: 'Vendor', value: 'vendor' },
  { label: 'Material Cost', value: 'cost' },
  { label: 'Material Markup', value: 'markup' },
  { label: 'Material Price', value: 'price' },
  { label: 'Labour Cost', value: 'labourCost' },
  { label: 'Labour Markup', value: 'labourMarkup' },
  { label: 'Labour Price', value: 'labourPrice' },
  { label: 'Quantity', value: 'quantity' },
  { label: 'Quantity Allocated', value: 'quantityAllocated' },
  { label: 'Quantity Available', value: 'quantityAvailable' },
  { label: 'Threshold', value: 'quantityThreshold' },
  { label: 'Unit Type', value: 'units' },
  { label: 'Cost Code', value: 'costcode' },
  { label: 'Locations', value: 'locations' },
];

const PROJECT_MATERIAL_COLUMN_OPTIONS = [
  { label: 'ID', value: 'code' },
  { label: 'Description', value: 'description' },
  { label: 'Part Number', value: 'partNumber' },
  { label: 'Supplier', value: 'supplier' },
  { label: 'Vendor', value: 'vendor' },
  { label: 'Material Cost', value: 'cost' },
  { label: 'Material Markup', value: 'markup' },
  { label: 'Material Price', value: 'price' },
  { label: 'Labour Cost', value: 'labourCost' },
  { label: 'Labour Markup', value: 'labourMarkup' },
  { label: 'Labour Price', value: 'labourPrice' },
  { label: 'Unit Type', value: 'units' },
  { label: 'Cost Code', value: 'costcode' },
];

const GLOBAL_STORAGE_KEY = 'materialColumns';
const PROJECT_STORAGE_KEY = 'projectMaterialColumns';

const GLOBAL_DEFAULT_HIDDEN = new Set(['quantityAllocated', 'quantityAvailable']);
const GLOBAL_DEFAULTS = GLOBAL_MATERIAL_COLUMN_OPTIONS
  .filter(({ value }) => !GLOBAL_DEFAULT_HIDDEN.has(value))
  .map((opt) => opt.value);
const PROJECT_DEFAULTS = PROJECT_MATERIAL_COLUMN_OPTIONS.map((opt) => opt.value);

export default function MaterialsColumnChanger({
  onChange: propChangeCallback,
  isProject = false,
}) {
  const resolvedDefaults = isProject ? PROJECT_DEFAULTS : GLOBAL_DEFAULTS;
  const storageKey = isProject ? PROJECT_STORAGE_KEY : GLOBAL_STORAGE_KEY;
  const options = isProject ? PROJECT_MATERIAL_COLUMN_OPTIONS : GLOBAL_MATERIAL_COLUMN_OPTIONS;

  const [selected, setSelected] = useState();
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const onChange = useCallback((key) => (newChecked) => {
    if (newChecked) {
      setSelected((prev) => {
        const newSelected = [...prev, key];
        window.localStorage.setItem(storageKey, JSON.stringify(newSelected));
        return newSelected;
      });
    } else {
      setSelected((prev) => {
        const newSelected = [...prev];
        const index = newSelected.indexOf(key);
        if (index > -1) newSelected.splice(index, 1);
        window.localStorage.setItem(storageKey, JSON.stringify(newSelected));
        return newSelected;
      });
    }
  }, [storageKey]);

  useEffect(() => {
    try {
      const rawChecked = window.localStorage.getItem(storageKey);
      const storedChecked = rawChecked ? JSON.parse(rawChecked) : resolvedDefaults;
      setSelected(storedChecked);
    } catch {
      setSelected(resolvedDefaults);
    }
  }, [storageKey, resolvedDefaults]);

  useEffect(() => {
    propChangeCallback(selected);
  }, [selected]);

  return (
    <Popover
      trigger="click"
      placement="bottomRight"
      content={(
        <div style={{ width: 175 }}>
          <Row>
            <PageHeader
              title="Columns"
              style={{ padding: 0, marginBottom: 5, fontFamily: 'raleway-medium' }}
            />
          </Row>
          <Col>
            {options.map(({ label, value }) => (
              <Row key={value}>
                <OnTraccrCheckbox
                  onChange={onChange(value)}
                  value={selectedSet.has(value)}
                  label={label}
                />
              </Row>
            ))}
          </Col>
        </div>
      )}
    >
      <OnTraccrButton
        title="Columns"
        icon={<SettingOutlined />}
        type="cancel"
      />
    </Popover>
  );
}

MaterialsColumnChanger.propTypes = {
  onChange: PropTypes.func.isRequired,
  isProject: PropTypes.bool,
};

MaterialsColumnChanger.defaultProps = {
  isProject: false,
};
